import type {
  AdminFlowDefinition,
  AdminFlowRun,
  AdminFlowRunSummary,
  AdminFlowTool,
} from "../models/admin-flows.models";
import {
  listAdminFlowRuns,
  listAdminFlowTools,
  startAdminFlowRun,
  stopAdminFlowRun,
} from "../services/admin-flows.service";
import { getAdminAgentBrowserTokenScope, getAdminAgentErrorMessage } from "../utils/admin-agent.utils";
import { useCallback, useEffect, useRef, useState } from "react";

// Poll serially while this scoped workspace is open; never retain an administrator's bearer for unattended work.
export const useAdminFlowRuns = (token: string | undefined) => {
  const [tools, setTools] = useState<AdminFlowTool[]>([]);
  const [runs, setRuns] = useState<AdminFlowRunSummary[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const active = useRef(true);
  const pending = useRef(false);
  const attempt = useRef<{ definition: string; requestId: string }>();
  const mutation = useRef(0);
  const session = useRef(0);
  let sessionScope = "";

  try {
    sessionScope = token ? getAdminAgentBrowserTokenScope(token) : "";
  } catch {
    sessionScope = "";
  }

  useEffect(() => {
    active.current = true;
    session.current += 1;
    mutation.current += 1;
    attempt.current = undefined;
    pending.current = false;
    setTools([]);
    setRuns([]);
    setError("");
    setBusy(false);

    // A different caller or Strict Mode remount must not make an older request current again.
    // Same-identity bearer renewal retains uncertain submission IDs so a retry cannot duplicate a paid run.
    return () => {
      active.current = false;
      session.current += 1;
    };
  }, [sessionScope]);

  useEffect(() => {
    const controller = new AbortController();
    listAdminFlowTools(token, controller.signal)
      .then((catalog) => {
        if (!controller.signal.aborted) setTools(catalog);
      })
      .catch((reason) => {
        if (!controller.signal.aborted) setError(getAdminAgentErrorMessage(reason));
      });

    return () => controller.abort();
  }, [token, refresh]);

  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      const version = mutation.current;

      try {
        const snapshots = await listAdminFlowRuns(token, controller.signal);

        if (!controller.signal.aborted && version === mutation.current) setRuns(snapshots);
      } catch (reason) {
        if (!controller.signal.aborted && version === mutation.current) {
          setRuns([]);
          setError(getAdminAgentErrorMessage(reason));
        }
      }

      if (!controller.signal.aborted) timer = setTimeout(poll, 2500);
    };
    void poll();

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [token, refresh]);

  const start = useCallback(
    async (flow: AdminFlowDefinition): Promise<AdminFlowRun | undefined> => {
      if (pending.current) return;

      pending.current = true;
      const startedSession = session.current;
      setBusy(true);
      setError("");
      mutation.current += 1;
      const definition = JSON.stringify(flow);

      if (attempt.current?.definition !== definition) attempt.current = { definition, requestId: crypto.randomUUID() };

      try {
        const run = await startAdminFlowRun(token, flow, attempt.current!.requestId);

        if (active.current && session.current === startedSession) {
          mutation.current += 1;
          setRuns((current) => [run, ...current.filter((entry) => entry.id !== run.id)]);
          attempt.current = undefined;

          return run;
        }
      } catch (reason) {
        if (active.current && session.current === startedSession) setError(getAdminAgentErrorMessage(reason));
      } finally {
        if (active.current && session.current === startedSession) {
          pending.current = false;
          setBusy(false);
        }
      }
    },
    [token],
  );

  const stop = async (id: string) => {
    const startedSession = session.current;
    mutation.current += 1;

    try {
      const run = await stopAdminFlowRun(token, id);

      if (active.current && session.current === startedSession) {
        mutation.current += 1;
        setRuns((current) => current.map((entry) => (entry.id === id ? run : entry)));
      }
    } catch (reason) {
      if (active.current && session.current === startedSession) setError(getAdminAgentErrorMessage(reason));
    }
  };

  return {
    tools,
    runs,
    error,
    busy,
    start,
    stop,
    reload: () => {
      setError("");
      setRefresh((value) => value + 1);
    },
  };
};
